import React from 'react';
import {useCheckboxState} from 'reakit/Checkbox';

import {emptyObject} from '@packages/utilities/fp/utils';
import {useDevAlert} from '@packages/systems/spring-hooks';

import {useFormGroupState, useFormGroupId} from '../FormGroup';
import {getDefinedValue} from '../../utils';
import {useSize} from '../../themes';
import {CheckboxGroupContext} from '../CheckboxGroup/CheckboxGroup';
import type {SizeContextSize} from '../__SharedTypes';

type UseCheckboxType = {
  defaultChecked?: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  isIndeterminate?: boolean;
  isRequired?: boolean;
  id?: string;
  name?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    meta: {
      nextIsChecked: boolean;
    }
  ) => void;
  size?: SizeContextSize;
  value?: string;
};

export const useCheckbox = ({
  defaultChecked,
  isChecked: isCheckedProp,
  isDisabled: isDisabledProp,
  isError: isErrorProp,
  isIndeterminate,
  isRequired: isRequiredProp,
  id: idProp,
  name: nameProp,
  onChange: onChangeProp,
  size: sizeProp,
  value,
}: UseCheckboxType) => {
  useDevAlert(
    Boolean(isCheckedProp) && Boolean(isIndeterminate),
    'Checkbox received a value of true for both isChecked and isIndeterminate. The isChecked value will be disregarded.'
  );

  const groupContext = React.useContext(CheckboxGroupContext);
  const {
    isError: isErrorFromCheckboxGroup,
    isDisabled: isDisabledFromCheckboxGroup,
    isRequired: isRequiredFromCheckboxGroup,
    name,
    onChangeGroup,
    values,
  } = groupContext || emptyObject;

  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const checkedFromCheckboxGroup = values ? values.includes(value) : null;
  const checked = getDefinedValue(isCheckedProp, checkedFromCheckboxGroup);

  const [isChecked, setIsChecked] = React.useState(
    checked ?? Boolean(defaultChecked)
  );

  const id = useFormGroupId(idProp);

  const {
    ariaDescribedBy,
    isError: isErrorStateFromFormGroup,
    isRequired: isRequiredStateFromFormGroup,
  } = useFormGroupState();

  const isError = getDefinedValue(
    isErrorStateFromFormGroup,
    isErrorFromCheckboxGroup,
    isErrorProp
  );
  const isRequired = getDefinedValue(
    isRequiredStateFromFormGroup,
    isRequiredFromCheckboxGroup,
    isRequiredProp
  );
  const isDisabled = getDefinedValue(
    isDisabledFromCheckboxGroup,
    isDisabledProp
  );

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    const nextIsChecked =
      checked == null ? !isChecked /* internal state */ : !checked; /* prop */

    if (checked == null) {
      // uncontrolled state, maintain internal checked state
      setIsChecked(nextIsChecked);
    }

    if (onChangeProp) {
      onChangeProp(event, {nextIsChecked});
    }

    if (onChangeGroup) {
      onChangeGroup(value, event);
    }
  };

  const size = useSize(sizeProp);

  const controlledCheckedState = isIndeterminate ? 'indeterminate' : isChecked;

  const reakitCheckboxProps = useCheckboxState({
    state: controlledCheckedState,
  });

  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  reakitCheckboxProps.state = controlledCheckedState;

  const checkboxName = getDefinedValue(nameProp, name);

  return {
    ariaDescribedBy,
    checked: checked == null ? isChecked : checked,
    id,
    name: checkboxName,
    onChange,
    isDisabled,
    isError,
    isRequired,
    size,
    shouldHideRequired:
      isRequiredStateFromFormGroup || isRequiredFromCheckboxGroup,
    reakitCheckboxProps,
  };
};
